import { useEffect, useState } from 'react';
import { X, ZoomIn, Compass, Sparkles, Rocket, Users, Bot, ArrowDown, PenLine, Layers, BookOpenCheck, ShieldCheck } from 'lucide-react';

// Four operating rules for working with AI — the input-side discipline that makes Discovery/Delivery
// actually pay off. Same wisdom behind Deliberate: AI is only as good as the humans steering it.
const AI_RULES = [
  {
    id: 'input',
    icon: PenLine,
    title: 'Describe what you want — precisely.',
    body: "AI is only as good as the input. Vague prompts get vague code. Spell out intent, constraints, and success criteria before you ask for anything.",
  },
  {
    id: 'architecture',
    icon: Layers,
    title: 'Be explicit about architecture and trade-offs.',
    body: "Don’t say “make it better.” Say “refactor into a service layer — trade more files for testability.” AI mirrors the rigour you bring.",
  },
  {
    id: 'learn',
    icon: BookOpenCheck,
    title: 'Make your project auto-learn from mistakes.',
    body: "Keep a LEARNINGS.md (and a CLAUDE.md). Every time something gets corrected, capture what went wrong, why, and the fix — then feed it back so the same mistake doesn’t happen twice.",
  },
  {
    id: 'verify',
    icon: ShieldCheck,
    title: "Don’t assume AI output is correct.",
    body: "Read the diff. Run the tests. Open the browser. Fast iteration with AI only works if verification is just as fast — otherwise you’re stacking bugs, not shipping features.",
  },
];

// Discovery and Delivery are the AI-first meat of the blueprint.
// Discovery = humans pour experienced judgement in; AI collaborates on PRDs, designs, and spikes.
// Delivery = AI codes and designs, humans consult and validate.
const AI_FIRST_MOMENTS = [
  {
    id: 'discovery',
    phase: 'Discovery',
    headline: 'Humans bring taste. AI explores the space.',
    icon: Sparkles,
    accent: 'border-blue-200 bg-blue-50',
    tagColor: 'bg-blue-100 text-blue-900',
    collaborators: 'Product · Design · Eng Lead',
    human: [
      'Frame the problem, constraints and success criteria',
      'Stress-test PRDs — Forming → Storming → Norming',
      'Review design signals at 30%, 60% and 90%',
      'Lead the spike — decide what is actually risky',
    ],
    ai: [
      "Draft and iterate on PRDs from the team's intent",
      'Generate design options, flows, and UX variants for review',
      'Run investigations and surface architectural trade-offs',
      'Produce the Architecture Change Record draft',
    ],
    outputs: 'Kickoff · Problem exploration · PRD Forming → Storming → Norming · Design Signal 30/60/90 · Eng feasibility / POC · Architecture Change Record',
  },
  {
    id: 'delivery',
    phase: 'Delivery',
    headline: 'AI codes and designs. Humans consult and ship.',
    icon: Rocket,
    accent: 'border-emerald-200 bg-emerald-50',
    tagColor: 'bg-emerald-100 text-emerald-900',
    collaborators: 'Eng Lead · Engineering · Design',
    human: [
      'Lock the design signal at 90% — experienced taste, not committee',
      'Consult: senior judgement on AI output, not ticket-pushing',
      'Own code review, integration, and the quality bar',
      'Decide what actually gets released',
    ],
    ai: [
      'Write code against a commitment-ready PRD and dev-ready design',
      'Implement across the codebase in collaboration with teammates',
      'Draft tests, migrations, and release notes',
      'Surface regressions and open questions back to the humans',
    ],
    outputs: 'MVP scope · Dev-Ready Design · Dev-Ready Architecture · PRD Commitment-Ready · Build, test, release · PRD Live',
  },
] as const;

const PHASES = [
  {
    id: 'exploration',
    number: 1,
    label: 'Exploration',
    icon: Compass,
    accent: 'border-amber-200 bg-amber-50',
    numberColor: 'bg-amber-200 text-amber-900',
    tagline: 'Frame the problem before touching AI.',
    aiFirst: false,
    steps: [
      { who: 'Product', what: 'Intake / Intent Framing' },
      { who: 'Stakeholders', what: 'Intent, constraints, success criteria' },
    ],
  },
  {
    id: 'discovery',
    number: 2,
    label: 'Discovery',
    icon: Sparkles,
    accent: 'border-blue-200 bg-blue-50',
    numberColor: 'bg-blue-200 text-blue-900',
    tagline: 'Humans and AI work the codebase together.',
    aiFirst: true,
    steps: [],
  },
  {
    id: 'delivery',
    number: 3,
    label: 'Delivery',
    icon: Rocket,
    accent: 'border-emerald-200 bg-emerald-50',
    numberColor: 'bg-emerald-200 text-emerald-900',
    tagline: 'AI codes. Humans consult, review, ship.',
    aiFirst: true,
    steps: [],
  },
] as const;

const ROLE_LENS = [
  { role: 'Product', color: 'bg-amber-400', focus: 'Problem framing, prioritization, PRD readiness' },
  { role: 'Design', color: 'bg-blue-500', focus: 'Experience confidence, usability risk closure' },
  { role: 'Eng Lead', color: 'bg-emerald-500', focus: 'Feasibility, architectural impact' },
  { role: 'Engineering', color: 'bg-emerald-600', focus: 'Delivery execution' },
];

export function BlueprintPage() {
  const [zoomed, setZoomed] = useState(false);

  // Modal: ESC to close and lock body scroll while open.
  useEffect(() => {
    if (!zoomed) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomed(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handler);
    };
  }, [zoomed]);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <header className="mb-8">
        <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">Blueprint</h1>
        <p className="mt-3 max-w-3xl text-base text-ink-secondary">
          The recipe for shipping AI-first products without the chaos — a{' '}
          <a
            href="https://deliberate.dadu.co/"
            target="_blank"
            rel="noreferrer"
            className="border-b border-dotted border-ink-tertiary text-ink hover:border-ink"
            title="Deliberate — Human-Governed AI Systems"
          >
            deliberate
          </a>{' '}
          collaboration where Product, Design, and Engineering work the codebase together with AI.
          Humans feed experienced judgement into Discovery; AI does the heavy lifting in Delivery
          while humans consult, review, and ship.
        </p>
        <p className="mt-2 max-w-3xl text-sm text-ink-tertiary">
          Use each column as a milestone. Skipping a step doesn&apos;t save time — it just pushes the
          rework later.
        </p>
      </header>

      {/* 1. Three phases at a glance — compact stepper doubling as a TOC */}
      <section className="mb-10" aria-labelledby="phases-heading">
        <h2
          id="phases-heading"
          className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary"
        >
          The three phases at a glance
        </h2>
        <ol className="mt-3 grid gap-3 md:grid-cols-3" role="list">
          {PHASES.map((p) => (
            <li
              key={p.id}
              className={`relative rounded-2xl border p-4 ${p.accent} ${
                p.aiFirst ? 'ring-1 ring-inset ring-white/60' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${p.numberColor}`}
                  aria-hidden="true"
                >
                  {p.number}
                </span>
                <p.icon className="h-4 w-4 text-ink" aria-hidden="true" />
                <h3 className="font-display text-base font-bold text-ink">{p.label}</h3>
                {p.aiFirst && (
                  <span className="ml-auto rounded-full bg-white/80 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-ink">
                    AI-first
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-ink-secondary">{p.tagline}</p>

              {p.steps.length > 0 ? (
                <ul className="mt-3 space-y-1 text-xs text-ink-secondary">
                  {p.steps.map((s, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="shrink-0 text-ink-tertiary">•</span>
                      <span>
                        <span className="font-semibold text-ink">{s.who}</span> — {s.what}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <a
                  href="#ai-first"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink hover:underline"
                >
                  See who does what <ArrowDown className="h-3 w-3" />
                </a>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* 2. Reference image — compact, opens full-size on click */}
      <figure className="mx-auto mb-10 max-w-4xl">
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="group relative block w-full overflow-hidden rounded-2xl border border-ring bg-white shadow-card transition-shadow hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-2"
          aria-label="Open full-size blueprint reference"
        >
          <img
            src="/blueprint.png"
            alt="Make Building Products Fun Again — blueprint showing Exploration, Discovery and Delivery phases across Product, Design, Eng Lead and Engineering roles."
            className="w-full"
            loading="lazy"
          />
          <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-medium text-ink shadow-sm backdrop-blur">
            <ZoomIn className="h-3 w-3" /> Open full size
          </span>
        </button>
        <figcaption className="mt-2 text-center text-xs text-ink-tertiary">
          The canonical visual — &ldquo;Make Building Products Fun Again.&rdquo;
        </figcaption>
      </figure>

      {/* 3. AI-first spotlight — the meat */}
      <section id="ai-first" className="mb-10 scroll-mt-20" aria-labelledby="ai-first-heading">
        <div className="mb-4 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
            The heart of AI-first
          </span>
          <h2
            id="ai-first-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            Where AI lives in the blueprint
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            Exploration stays human — AI can&apos;t frame a problem it doesn&apos;t understand. The
            real leverage is in <strong className="text-ink">Discovery</strong> and{' '}
            <strong className="text-ink">Delivery</strong>: teams collaborate on the codebase with
            AI, experienced humans steering.
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {AI_FIRST_MOMENTS.map((m) => (
            <article
              key={m.id}
              className={`rounded-2xl border p-5 ${m.accent}`}
              aria-labelledby={`ai-first-${m.id}-heading`}
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider ${m.tagColor}`}
                >
                  <m.icon className="h-3 w-3" aria-hidden="true" /> {m.phase}
                </span>
                <span className="text-2xs uppercase tracking-wider text-ink-tertiary">
                  {m.collaborators}
                </span>
              </div>
              <h3
                id={`ai-first-${m.id}-heading`}
                className="mt-3 font-display text-lg font-bold text-ink"
              >
                {m.headline}
              </h3>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-white/80 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                    <Users className="h-3.5 w-3.5" aria-hidden="true" /> Humans bring
                  </div>
                  <ul className="mt-2 space-y-1.5 text-xs text-ink-secondary">
                    {m.human.map((h, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="shrink-0 text-ink-tertiary">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl bg-white/80 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                    <Bot className="h-3.5 w-3.5" aria-hidden="true" /> AI does
                  </div>
                  <ul className="mt-2 space-y-1.5 text-xs text-ink-secondary">
                    {m.ai.map((a, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="shrink-0 text-ink-tertiary">•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-3 text-2xs text-ink-tertiary">
                <span className="font-semibold uppercase tracking-wider">Milestones:</span>{' '}
                {m.outputs}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* 4. Four rules for working with AI — input-side discipline */}
      <section className="mb-10" aria-labelledby="rules-heading">
        <div className="mb-5 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
            Operating rules
          </span>
          <h2
            id="rules-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            Four rules for working with AI
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            The blueprint above shows <em>when</em> AI fits. These rules are <em>how</em> to actually
            work with it — the discipline that separates leverage from liability.
          </p>
          <blockquote className="mt-4 rounded-2xl border-l-4 border-ink bg-accent-subtle p-4 sm:p-5">
            <p className="font-display text-base leading-relaxed text-ink sm:text-lg">
              &ldquo;AI is only as good as the input. Remember to describe what you want, be
              specific on architecture choices and tradeoffs. Setup your project to auto-learn
              from mistakes to prevent them happening again. Don&apos;t assume AI output is
              correct.&rdquo;
            </p>
          </blockquote>
        </div>
        <ol className="grid gap-3 sm:grid-cols-2" role="list">
          {AI_RULES.map((r, i) => (
            <li
              key={r.id}
              className="flex gap-3 rounded-2xl border border-ring bg-surface-raised p-4 shadow-card"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-subtle">
                <r.icon className="h-4 w-4 text-ink" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
                    Rule {i + 1}
                  </span>
                </div>
                <h3 className="mt-0.5 font-display text-base font-bold text-ink">{r.title}</h3>
                <p className="mt-1 text-sm text-ink-secondary">{r.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 5. Role lens — compact */}
      <section className="mb-10" aria-labelledby="roles-heading">
        <h2
          id="roles-heading"
          className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary"
        >
          Who focuses on what
        </h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {ROLE_LENS.map((r) => (
            <div
              key={r.role}
              className="rounded-xl border border-ring bg-surface-raised p-3 shadow-card"
            >
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${r.color}`} aria-hidden="true" />
                <h3 className="text-sm font-semibold text-ink">{r.role}</h3>
              </div>
              <p className="mt-1 text-xs text-ink-secondary">{r.focus}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Using this in the hackathon — actionable rules */}
      <section
        className="rounded-2xl border border-ring bg-accent-subtle p-5 sm:p-6"
        aria-labelledby="usage-heading"
      >
        <h2 id="usage-heading" className="font-display text-lg font-bold text-ink">
          Using this in the hackathon
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-ink-secondary">
          <li className="flex gap-2">
            <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ink" />
            <span>
              <strong className="text-ink">Before you let AI code:</strong> is the PRD past Norming?
              Is the design at 90%? If not, you&apos;re asking AI to guess at your taste.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ink" />
            <span>
              <strong className="text-ink">During Discovery:</strong> bring humans with domain
              experience into the same session as AI — AI drafts, humans steer. That&apos;s where the
              leverage is.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ink" />
            <span>
              <strong className="text-ink">During Delivery:</strong> the Eng Lead stops doing and
              starts consulting. AI writes, humans review, the whole team owns the codebase together.
            </span>
          </li>
        </ul>
      </section>

      {/* Zoom overlay */}
      {zoomed && (
        <div
          className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black/80 p-4"
          onClick={() => setZoomed(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Blueprint full-size view"
        >
          <button
            type="button"
            onClick={() => setZoomed(false)}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src="/blueprint.png"
            alt="Make Building Products Fun Again — blueprint (full size)"
            className="max-h-full max-w-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
